import React, { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";

import { Text, TextProps } from "../../../styles/utility-components";

interface EditableTextProps {
  textProps: TextProps;
  placeholder?: string;
  emptyText?: string;
  onSave?: (value: string) => void;
  autoFocus?: boolean;
  padding?: CSSProperties["padding"];
}
// TODO: update this to take a form control as a prop to bring it inline with our other form inputs
export default function EditableText({
  textProps,
  placeholder,
  emptyText = "Empty text",
  onSave,
  autoFocus = false,
  padding,
}: EditableTextProps) {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState("");

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    setValue(textProps.children?.toString() || "");
  };

  const handleChange = (val: React.ChangeEvent<HTMLTextAreaElement> | FieldValues) => {
    setValue(typeof val === "object" && "target" in val ? val.target.value : val.toString());
  };

  const handleSave = useCallback(() => {
    if (value.trim() === "") {
      setValue(textProps.children?.toString() || "");
      setIsEditing(false);
      return;
    }

    if (value === textProps.children?.toString()) {
      setIsEditing(false);
      return;
    }

    setIsEditing(false);
    onSave?.(value);
  }, [value, textProps.children, onSave]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isEditing &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        handleSave();
      }
    };

    if (isEditing) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing, handleSave]);

  const textToDisplay = () => {
    if (textProps.children?.toString() === "") {
      return (
        <EmptyText body color={theme.colors.sysTextSecondary}>
          {emptyText}
        </EmptyText>
      );
    }

    return <Text {...textProps} />;
  };

  return (
    <div ref={containerRef}>
      {isEditing ? (
        <Text {...textProps} as="div">
          <StyledTextArea
            autoFocus={autoFocus}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            rows={1}
            placeholder={placeholder}
            style={{ height: "auto" }}
            padding={padding}
            ref={(textarea) => {
              if (textarea) {
                textarea.style.height = "auto";
                textarea.style.height = `${textarea.scrollHeight}px`;
                if (autoFocus) {
                  setTimeout(() => textarea.focus(), 0);
                }
              }
            }}
          />
        </Text>
      ) : (
        <TextContainer padding={padding} onClick={handleClick}>
          {textToDisplay()}
        </TextContainer>
      )}
    </div>
  );
}

const StyledTextArea = styled.textarea<{ padding?: CSSProperties["paddingInline"] }>((props) => ({
  width: "100%",
  padding: props.padding || "8px 12px",
  lineHeight: "22px",
  border: "1px solid #d9d9d9",
  borderRadius: "4px",
  backgroundColor: "#fff",
  transition: "all 0.2s ease-in-out",
  outline: "none",
  resize: "none",
  overflow: "hidden",
  minHeight: "38px",
  fontFamily: "inherit",
  fontSize: "inherit",
  fontWeight: "inherit",
  color: "inherit",

  "&:hover": {
    borderColor: "#40a9ff",
  },

  "&:focus": {
    borderColor: "#1890ff",
    boxShadow: "0 0 0 2px rgba(24, 144, 255, 0.2)",
  },
}));

const EmptyText = styled(Text)(({ theme }) => ({
  color: theme.colors.sysTextSecondary,

  fontStyle: "italic",
}));

const TextContainer = styled.div<{ padding?: CSSProperties["paddingInline"] }>(
  ({ padding, theme }) => ({
    cursor: "pointer",
    padding: padding || "8px 12px",
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",

    "&:hover": {
      borderRadius: "4px",
      backgroundColor: theme.colors.sysPrimarySubtle,
    },

    transition: "all 0.2s ease-in-out",
  }),
);
